import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import * as pdfjs from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry.js';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';

import { GlobalWorkerOptions } from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function PdfViewerModal({ isOpen, onClose, fileUrl }) {
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;
    
    const [pdfData, setPdfData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (fileUrl && isOpen) {
            setLoading(true);
            setError(null);
            
            fetch(fileUrl)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to load PDF: ${response.status}`);
                    }
                    return response.blob();
                })
                .then(blob => {
                    const url = URL.createObjectURL(blob);
                    setPdfData(url);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error loading PDF:', err);
                    setError(err.message);
                    setLoading(false);
                });
        }
        
        return () => {
            if (pdfData) {
                URL.revokeObjectURL(pdfData);
                setPdfData(null);
            }
        };
    }, [fileUrl, isOpen]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            if (pdfData) {
                URL.revokeObjectURL(pdfData);
            }
            setPdfData(null);
            setError(null);
            setLoading(false);
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="PDF Viewer"
            style={{
                overlay: {
                    zIndex: 10000,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                },
                content: { 
                    width: '70%', 
                    height: '80%', 
                    margin: 'auto',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    zIndex: 10001
                }
            }}
        >
            {/* Fixed Header - Close button and Toolbar */}
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                    borderBottom: '1px solid #e0e0e0',
                    padding: '10px 16px',
                    flexShrink: 0
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <button
                        onClick={onClose}
                        style={{ 
                            borderRadius: '4px', 
                            padding: '6px 12px',
                            border: 'none',
                            fontFamily: 'Prometo Trial',
                            color: '#5c5c5c',
                            cursor: 'pointer',
                            backgroundColor: '#f0f0f0'
                        }}
                    >
                        Close
                    </button>
                    
                    {pdfData && (
                        <Worker workerUrl={pdfjsWorker}>
                            <Toolbar>
                                {(props) => {
                                    const {
                                        CurrentPageInput,
                                        GoToNextPage,
                                        GoToPreviousPage,
                                        Zoom,
                                        Print,
                                        Download,
                                    } = props;

                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <GoToPreviousPage />
                                            <CurrentPageInput />
                                            <GoToNextPage />
                                            <Zoom />
                                            <Print />
                                            <Download />
                                        </div>
                                    );
                                }}
                            </Toolbar>
                        </Worker>
                    )}
                </div>
            </div>

            {/* Scrollable PDF Content */}
            <div
                style={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                {loading && (
                    <div style={{ color: '#5c5c5c', fontFamily: 'Prometo Trial' }}>
                        Loading PDF...
                    </div>
                )}
                
                {error && (
                    <div style={{ color: 'red', fontFamily: 'Prometo Trial' }}>
                        {error}
                    </div>
                )}
                
                {pdfData && !loading && !error && (
                    <Worker workerUrl={pdfjsWorker}>
                        <Viewer
                            fileUrl={pdfData}
                            plugins={[toolbarPluginInstance]}
                            enableForms={true}
                            defaultScale={0.8}
                        />
                    </Worker>
                )}
            </div>
        </Modal>
    );
}

export default PdfViewerModal;